/**
 * deptUtils.js — 학과 기반 수강 가능 여부 판단 유틸리티
 *
 * courseData.js의 `department` 필드는 개설학과명(estbDpmjNm)을 의미한다.
 * 해당 값이 특정 학과명이면 그 학과 학생 대상 분반이고,
 * "교양" / "교양(야)" 이면 공통 수강 가능이다.
 * isOnline === true 이면 이러닝 공통 수강 가능이다.
 *
 * atlecDpmjLmttYn(학과제한여부) 필드는 크롤링 시 누락되었으므로
 * department + isOnline + category 조합으로 대신 판단한다.
 */

import { REQUIREMENT_DATA, ALL_REQUIREMENTS } from '../data/requirementData'
import { GRADUATION_RULES_2026 } from '../data/requirements/graduationRules2026'
import { COLLEGES, DEPT_META_MAP } from '../data/requirements/colleges'
import { getCreditRequirements2026, getDeptRequirements2026, REQUIREMENTS_2026 } from '../data/requirements/requirements2026'

// ── 학과 키워드 매핑 테이블 ──────────────────────────────────────────────────
// DEPARTMENTS.name(UI 선택값) → courseData.department에서 "내 학과"로 볼 문자열 목록
const DEPT_KEYWORD_MAP = {
  '경영공학학부':       ['경영', '경영공학대학', '경영학부', '경영학과', '경영공학학부'],
  '컴퓨터공학부':       ['컴퓨터공학', '컴퓨터공학부', '컴퓨터학부', '컴퓨터SW'],
  'AI·소프트웨어학부':  ['AI데이터과학부', '데이터과학부', '미디어SW', 'AI·소프트웨어학부'],
  '전자공학부':         ['전기전자공학부', '전자공학', '전기공학', '전자공학부', '전자물리', '전자재료공학'],
  '디자인학부':         ['디자인학부', '커뮤니케이션디자인', '패션디자인', '공예디자인'],
  '기계공학부':         ['기계공학', '기계공학부', '산업공학', '산업 및 기계공학부'],
  '화학공학과':         ['화학공학', '화학공학·신소재공학부', '신소재공학', '융합화학산업'],
  '토목환경공학부':     ['건설환경공학', '건설환경에너지공학부', '토목환경공학부', '환경에너지공학'],
}

// "교양" 전용 개설학과 문자열 — 이 값이면 공통 수강 가능
const OPEN_DEPT_VALUES = new Set(['교양', '교양(야)'])

// ── 정규화 ───────────────────────────────────────────────────────────────────

/** 공백 제거만 수행 (안전) */
export function normalizeDeptName(name) {
  if (!name) return ''
  return String(name).trim().replace(/\s+/g, '')
}

/** 접미어(학부/학과/대학/전공) 제거 후 코어만 추출 */
export function extractDeptCore(name) {
  const n = normalizeDeptName(name)
  return n.replace(/(학부|학과|대학원|대학|전공|학원)$/, '')
}

// ── 학과 키워드 조회 ─────────────────────────────────────────────────────────

/**
 * 사용자가 선택한 학과명 → courseData.department에서 매칭할 문자열 목록 반환
 * 알 수 없는 학과면 extractDeptCore 결과를 키워드로 사용 (fallback)
 *
 * 온보딩 화면(OnboardingScreen)이 실제로 쓰는 학과 선택지는 requirements2026.js의
 * 53개 departmentName이다(DEPT_KEYWORD_MAP의 구 8개 단과대 체계가 아니다). courseData의
 * 개설학과명(estbDpmjNm)은 과목에 따라 학과 단위("기계공학부")로도, 단과대 단위(대규모 공통
 * 개설 과목은 "경영공학대학"/"인문사회융합대학"처럼)로도 나타난다. 그래서 selectedDept가
 * requirements2026 학과명이면 그 소속 단과대명과, REQ2026_ALIAS_MAP에 등록된 courseData 쪽
 * 별칭(있으면)도 함께 키워드에 포함시킨다 — "경영" 같은 넓은 substring이 아니라 실제 학사
 * 조직도(단과대-학과)에 근거한 매칭이라 다른 계열 학과를 잘못 합치지 않는다.
 */
export function getDeptKeywords(selectedDept) {
  if (!selectedDept) return []
  // 별칭 정규화 후 조회 — "경영공학"/"경영공학대학"/"경영학과" 등도 "경영공학학부"로 통일
  const canonical = normalizeDepartmentName(selectedDept)
  const known = DEPT_KEYWORD_MAP[canonical] ?? DEPT_KEYWORD_MAP[selectedDept]
  const base = known ?? (() => {
    // fallback: core 키워드를 기준으로 부분 매칭
    const core = extractDeptCore(canonical)
    return core ? [canonical, core] : [canonical]
  })()

  const college = REQ2026_DEPT_TO_COLLEGE.get(selectedDept)
  const courseDataAliases = REQ2026_REVERSE_ALIAS_MAP.get(selectedDept) ?? []
  if (!college && !courseDataAliases.length) return base
  return [...new Set([...base, selectedDept, ...(college ? [college] : []), ...courseDataAliases])]
}

// ── 공통/이러닝 여부 ─────────────────────────────────────────────────────────

/** 이러닝 과목 여부 */
export function isOnlineCourse(course) {
  return course?.isOnline === true
}

/**
 * 공통 수강 가능 과목 여부
 * - 이러닝 or
 * - department === "교양" / "교양(야)" or
 * - department가 비어 있거나 없으면 unknown으로 취급 (open이 아님)
 */
export function isCommonCourse(course) {
  if (!course) return false
  if (course.isOnline) return true
  const dept = normalizeDeptName(course.department)
  return OPEN_DEPT_VALUES.has(dept)
}

// ── 과목 수강 태그 ────────────────────────────────────────────────────────────

/**
 * 과목의 수강 가능 태그를 반환한다.
 * @returns {'online' | 'open' | 'my-dept' | 'other-dept' | 'unknown'}
 */
export function getCourseTag(course, selectedDept) {
  if (!course) return 'unknown'

  // 이러닝 — 공통
  if (course.isOnline) return 'online'

  const dept = normalizeDeptName(course.department)

  // 교양(공통) 개설
  if (!dept || OPEN_DEPT_VALUES.has(dept)) return 'open'

  // 학과 선택 없음 → unknown
  if (!selectedDept) return 'unknown'

  // 내 학과 키워드 매칭
  const keywords = getDeptKeywords(selectedDept)
  const matched = keywords.some(k => {
    const kn = normalizeDeptName(k)
    return dept === kn || dept.includes(kn) || kn.includes(dept)
  })

  return matched ? 'my-dept' : 'other-dept'
}

// ── 수강 가능 여부 단순 판단 ─────────────────────────────────────────────────

export function isCourseAvailableForDept(course, selectedDept) {
  const tag = getCourseTag(course, selectedDept)
  return tag !== 'other-dept'
}

/**
 * course.targetGrade(원본 trgtGrdeCd, 1~4) 기준으로 학생 학년과 맞는지 판단한다.
 * targetGrade가 없거나(대상학년 미지정) selectedGrade를 모르면 공통 후보로 허용한다.
 * 과목명/분반번호 문자열로 학년을 추측하지 않는다 — targetGrade 필드만 근거로 쓴다.
 */
export function isCourseAvailableForGrade(course, selectedGrade) {
  if (!course) return false
  const target = Number.isInteger(course.targetGrade) ? course.targetGrade : null
  if (target == null) return true
  if (selectedGrade == null) return true
  const grade = Number(selectedGrade)
  if (!Number.isFinite(grade)) return true
  return target === grade
}

// ── 필터링 ───────────────────────────────────────────────────────────────────

/**
 * 학과 기준으로 과목 배열 필터
 * @param {object[]} courses
 * @param {string|null} selectedDept  사용자가 선택한 학과명
 * @param {object} options
 * @param {boolean} options.showAll  true 이면 타 학과 분반도 포함
 */
export function filterCoursesByDept(courses, selectedDept, { showAll = false } = {}) {
  if (!Array.isArray(courses)) return []
  if (!selectedDept || showAll) return courses
  return courses.filter(c => getCourseTag(c, selectedDept) !== 'other-dept')
}

// ── 우선순위 정렬 ────────────────────────────────────────────────────────────

const TAG_PRIORITY = { 'my-dept': 0, 'open': 1, 'online': 2, 'unknown': 3, 'other-dept': 4 }

/**
 * 내 학과 → 공통 → 이러닝 → 미확인 → 타 학과 순으로 정렬, 같은 학과 우선순위 내에서는
 * 내 학년 대상(targetGrade 일치 또는 학년 무관) 분반을 먼저 보여준다.
 * 마지막 동률은 정원이 작은 과목(희소) 우선.
 * 필터가 아니라 정렬이므로 다른 학과/학년 항목도 그대로 남는다("선택 변경"/"검색" 화면 전용).
 */
export function sortCoursesByDeptPriority(courses, selectedDept, selectedGrade = null) {
  if (!Array.isArray(courses)) return courses
  if (!selectedDept && selectedGrade == null) return courses
  return [...courses].sort((a, b) => {
    const pa = TAG_PRIORITY[getCourseTag(a, selectedDept)] ?? 5
    const pb = TAG_PRIORITY[getCourseTag(b, selectedDept)] ?? 5
    if (pa !== pb) return pa - pb
    const ga = isCourseAvailableForGrade(a, selectedGrade) ? 0 : 1
    const gb = isCourseAvailableForGrade(b, selectedGrade) ? 0 : 1
    if (ga !== gb) return ga - gb
    // 정원 적은 순 (희소 분반 우선)
    const ca = a.capacity ?? 999
    const cb = b.capacity ?? 999
    return ca - cb
  })
}

/**
 * "직접 만들기"/"과목 추가"/"과목 찾기" 검색 화면 전용 — 조건 일치 과목을 앞에, 나머지를 뒤에 두고
 * 각 그룹 내부는 과목명 가나다순으로 정렬한다. 필터가 아니라 정렬이므로 조건 불일치 과목(다른 학과·
 * 다른 학년)도 그대로 남는다 — "학과/학년 미확정 프로필 → 강좌 0개" 같은 hard filter 사고를 만들지
 * 않기 위한 경로. selectedDept/selectedGrade/selectedCollege가 전부 없으면(프로필 미확정) 원본
 * 순서를 그대로 반환한다(정렬할 기준이 없으므로).
 */
export function sortCoursesByMatchThenAlphabetical(courses, selectedDept, selectedGrade = null, selectedCollege = null) {
  if (!Array.isArray(courses)) return courses
  if (!selectedDept && !selectedCollege && selectedGrade == null) return courses
  const isMatch = course => {
    const deptOk = selectedDept
      ? getCourseTag(course, selectedDept) === 'my-dept'
      : selectedCollege
      ? isCourseAvailableForCollege(course, selectedCollege)
      : true
    return deptOk && isCourseAvailableForGrade(course, selectedGrade)
  }
  const byName = (a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'ko')
  const matched = []
  const rest = []
  for (const course of courses) (isMatch(course) ? matched : rest).push(course)
  return [...matched.sort(byName), ...rest.sort(byName)]
}

/**
 * "분반·교수 설정" Sheet 표시 전용 — 이미 eligibility로 좁혀진 sections를 내 학과 묶음 하나 +
 * 나머지 학과 묶음(가나다순)으로 나눈다. 필터가 아니라 표시 순서/그룹핑이므로 아무것도 제거하지
 * 않는다. 같은 학과 안의 분반 순서는 입력 순서를 그대로 보존한다(courseData는 이미 분반번호순).
 * @param {object[]} sections  eligibility로 좁혀진 분반 배열
 * @param {string|null} selectedDept  사용자가 선택한 학과명
 * @returns {{ myDept: object[], otherGroups: { department: string, sections: object[] }[] }}
 */
export function groupSectionsByDeptForDisplay(sections, selectedDept) {
  if (!Array.isArray(sections)) return { myDept: [], otherGroups: [] }
  const myDept = []
  const othersByDept = new Map()
  for (const section of sections) {
    if (selectedDept && getCourseTag(section, selectedDept) === 'my-dept') {
      myDept.push(section)
      continue
    }
    const dept = section.department?.trim() || '개설학과 미정'
    if (!othersByDept.has(dept)) othersByDept.set(dept, [])
    othersByDept.get(dept).push(section)
  }
  const otherGroups = [...othersByDept.entries()]
    .map(([department, deptSections]) => ({ department, sections: deptSections }))
    .sort((a, b) => a.department.localeCompare(b.department, 'ko'))
  return { myDept, otherGroups }
}

// ── 프로필 기반 후보 축소 ─────────────────────────────────────────────────────
//
// 필드 의미(2026-2 라이브 포털 API 실측, info.suwon.ac.kr/estbLectDtai/listVEstbLectDtai.do
// 익명 세션으로 직접 조회 — 2103건 전수):
//
// - course.department(estbDpmjNm, 개설학과명): "수강 자격/제한"이 확인된 필드가 아니라
//   "개설학과 기반 관련도" 신호일 뿐이다. 다른 학과 분반은 등록 불가라고 하드 필터링하면 안 된다.
//
// - course.departmentLimit(atlecDpmjLmttYn, 학과제한여부): raw 값은 'Y'/'N'이며 boolean|null로
//   캡처한다(workers/suwon-course-sync/src/courseSync.js normalizeDepartmentLimit). 하지만 2026-2
//   전체 2103건을 실측한 결과 단 1건도 'Y'가 없었다 — 'Y'가 실제로 무엇을 의미하는지(진짜 타학과
//   등록 불가인지) 확인할 표본이 아예 없다. 그래서 departmentLimit은 캡처만 하고 어떤 제외 로직에도
//   쓰지 않는다. 표본이 생기면(어느 학기든 'Y'가 관측되면) 그때 의미를 재검증하고 제한 로직에 쓴다.
//
// - course.targetGrade(trgtGrdeCd, 대상학년코드): 1~4 정수로 실제 채워져 있다(2026-2 실측 분포
//   1학년 800 / 2학년 616 / 3학년 440 / 4학년 247, 결측 0건). 과목명/분반번호로 학년을 추측하지
//   않고 이 필드만 근거로 쓴다. 같은 subjectKey 안에서 학년이 갈리는 과목은 드물지만 실존한다
//   (2026-2 실측: 1057개 과목 중 15개, 예: "경영통계" 2·3학년, "대위법2" 1·2학년).
//
// 위 근거로 정책을 나눈다:
// - 추천/분반 설정: 학년과 프로필 소속(학과가 없으면 단과대) 기준 eligibility는 hard filter다.
//   정상 프로필에서 0건이면 데이터 불일치로 남겨야 하며 원본 전체로 fallback하지 않는다.
// - "선택 변경"/"내 분반 직접 선택"의 기본 노출: 분반 수가 많은 과목(예: AI활용ESL2 94개)은
//   getEligibleSectionsForStudent로 좁힌 개인화 후보를 기본값으로 보여주되, 반드시 "전체 분반
//   N개 보기" escape hatch를 함께 두어 언제든 전체 목록/검색 결과에 닿을 수 있게 한다 — 영구
//   제거가 아니라 기본값 축소이므로 department를 confirmed 제한처럼 쓰는 것과 다르다.
// - "과목 검색해서 추가": 하드 필터 금지 — sortCoursesByDeptPriority로 내 학과+내 학년
//   관련 항목을 앞으로 정렬만 하고 전체 검색 결과를 유지한다.

/**
 * 추천 엔진과 분반 설정에서 쓸 분반 후보를 학년→학과(학과가 없으면 단과대) 순으로 좁힌다.
 * 정상 프로필 필터 결과가 0건이면 빈 목록을 그대로 반환한다. 전체 분반 fallback은 수강 가능
 * 데이터 불일치를 숨겨 잘못된 분반을 추천하므로 금지한다.
 * @param {object[]} items  courseData 분반 배열(동일 subjectKey의 여러 분반)
 * @param {{ college?: string|null, department?: string|null, grade?: string|number|null }} student
 * @returns {object[]} 학년/소속 기준 eligible 후보
 */
export function getEligibleSectionsForStudent(items, { college, department, grade } = {}) {
  if (!Array.isArray(items)) return []
  let pool = items
  if (grade != null) {
    pool = pool.filter(c => isCourseAvailableForGrade(c, grade))
  }
  if (department) {
    pool = pool.filter(c => isCourseAvailableForDept(c, department))
  } else if (college) {
    pool = pool.filter(c => isCourseAvailableForCollege(c, college))
  }
  return pool
}

/**
 * 학년 하드 필터만 적용한다(department는 건드리지 않음) — "다른 학과 보기" 같은 표시용 전체 목록과
 * courseSelectionAdapter의 명시 선택 검증이 같은 정책을 공유하는 단일 지점이다.
 */
export function filterByGradeEligibility(sections, grade) {
  if (!Array.isArray(sections)) return []
  return grade != null ? sections.filter(section => isCourseAvailableForGrade(section, grade)) : sections
}

/**
 * 과목 추가/분반 변경의 기본 selectedSectionIds. department와 college가 둘 다 없으면(신규/미확정
 * 프로필) 절대 자동 선택하지 않는다 — 예전에는 이때 학년 eligible 전체로 fallback했는데, ESL2처럼
 * 학과별로 갈리는 대량 과목(94개)에서는 그 전체가 그대로 저장돼 사고가 됐다.
 * department가 있으면 내 학과(getCourseTag==='my-dept') 분반만, department가 없고 college만
 * 있으면(1학년 college-only 등 정상 상태) college 기준 eligible 분반 전체를 기본 선택한다 —
 * getEligibleSectionsForStudent가 이미 department→college 순으로 좁혀주므로 그 결과를 그대로
 * 쓴다. department가 있는데 내 학과 분반이 하나도 없으면(공통/교양 개설이라 my-dept 태그가 안
 * 붙는 경우 등) 그 department로 좁혀진 eligibility 전체로 fallback한다 — 이 pool은 이미
 * department로 걸러진 상태라 "다른 학과 분반이 섞여 들어오는" 문제가 재발하지 않는다.
 * "표시용으로 자른 목록"이 아니라 selection.selectedSectionIds에 그대로 저장되는 hard filter다.
 */
export function getDefaultSectionIdsForStudent(sections, { college, department, grade } = {}) {
  if (!department && !college) return []
  const eligible = getEligibleSectionsForStudent(sections, { college, department, grade })
  const myDept = eligible.filter(section => getCourseTag(section, department) === 'my-dept')
  return (myDept.length ? myDept : eligible).map(section => section.id)
}

/**
 * "선택 변경"류 화면에서 개인화된(기본) 분반 묶음에 붙일 라벨.
 * 예: "내 학과 · 1학년 분반 9개" / department·grade 둘 다 없으면 "관련 분반 N개".
 */
export function personalizedSectionsLabel(department, grade, count) {
  const parts = []
  if (department) parts.push('내 학과')
  if (grade != null) parts.push(`${grade}학년`)
  return parts.length ? `${parts.join(' · ')} 분반 ${count}개` : `관련 분반 ${count}개`
}

// ── UI 뱃지 설정 ─────────────────────────────────────────────────────────────

/**
 * 과목 카드에 표시할 뱃지 설정 반환
 * @returns {{ label: string, lightClass: string, darkClass: string } | null}
 */
export function getDeptBadge(course, selectedDept) {
  const tag = getCourseTag(course, selectedDept)
  switch (tag) {
    case 'my-dept':
      return {
        label:      '내 학과',
        lightClass: 'bg-blue-50 text-blue-700 border-blue-200',
        darkClass:  'bg-blue-900/30 text-blue-300 border-blue-800/40',
      }
    case 'open':
      return {
        label:      '공통',
        lightClass: 'bg-green-50 text-green-700 border-green-200',
        darkClass:  'bg-green-900/30 text-green-300 border-green-800/40',
      }
    case 'online':
      return {
        label:      '이러닝',
        lightClass: 'bg-gray-100 text-gray-600 border-gray-200',
        darkClass:  'bg-gray-800 text-gray-300 border-gray-700',
      }
    case 'other-dept':
      return {
        label:      '타 학과',
        lightClass: 'bg-red-50 text-red-600 border-red-200',
        darkClass:  'bg-red-900/30 text-red-400 border-red-800/40',
      }
    case 'unknown':
    default:
      return null  // 뱃지 없음 (학과 정보 불명확)
  }
}

/**
 * getPlanBOptions에서 대체 과목 reason 문자열에 붙일 학과 라벨
 */
export function getDeptReasonLabel(course, selectedDept) {
  const tag = getCourseTag(course, selectedDept)
  if (tag === 'my-dept')    return '내 학과 대상'
  if (tag === 'open')       return '공통 교양'
  if (tag === 'online')     return '이러닝'
  if (tag === 'other-dept') return '타 학과 분반'
  return null
}

// ── 필수과목 화면 전용 ─────────────────────────────────────────────────────────

// ── 필수과목 지원 여부 ─────────────────────────────────────────────────────────
// 레거시 화이트리스트 — 과목코드(subjectKey)가 없는 구형 데이터에 대한 폴백용으로만 사용
const LEGACY_REQUIRED_WHITELIST = new Set([
  'AI활용ESL1', 'AI활용CSL1', 'AI활용글쓰기',
  '경영학원론', '경제학원론', '마케팅원론', '회계원리',
])

/** 분반 번호 상한: 이 값 초과이면 대량 공통/교환 분반으로 간주하여 숨김 */
const REQUIRED_SECTION_MAX_DIV = 49

/**
 * 이수요건 데이터의 requiredSubject 항목이 분반 선택 기능 지원 대상인지 확인.
 *
 * 우선순위:
 *  1. courseData 자동 생성(_autoGenerated) 항목 → 항상 지원
 *  2. 과목코드(subjectKey)가 있으면 courseData와 정확 매칭 가능 → 지원
 *  3. 레거시 화이트리스트 폴백 (과목코드 없는 구형 데이터용)
 */
export function isSupportedRequiredCourse(rs) {
  if (!rs) return false
  if (rs._autoGenerated) return true
  if (rs.subjectKey) return true
  return LEGACY_REQUIRED_WHITELIST.has(String(rs.displayName ?? '').trim())
}

/**
 * 분반 번호가 REQUIRED_SECTION_MAX_DIV 초과인 대량 공통/교환 분반인지 확인
 */
export function isHighCommonSection(course) {
  if (!course) return false
  const div = parseInt(course.division, 10)
  return !isNaN(div) && div > REQUIRED_SECTION_MAX_DIV
}

/**
 * 필수과목 화면에서 사용하는 엄격한 분반 필터
 * - 대량 공통 분반 (div > 49) 제외
 * - 교양 개설 분반 (open 태그) 제외
 * - 내 학과(my-dept) 분반만 포함
 */
export function filterRequiredCourseSections(sections, selectedDept) {
  if (!Array.isArray(sections)) return []
  return sections.filter(c => {
    if (isHighCommonSection(c)) return false
    const tag = getCourseTag(c, selectedDept)
    return tag === 'my-dept'
  })
}

/**
 * 교양/선택과목 화면 필터 (filterCoursesByDept 위임)
 */
export function filterElectiveCourseSections(courses, selectedDept, options = {}) {
  return filterCoursesByDept(courses, selectedDept, options)
}

// ── 전체 학과 목록 (courseData 기반) ─────────────────────────────────────────

const EXCLUDE_DEPT_SET = new Set(['교양', '교양(야)'])

/**
 * courseData에서 고유 학과명 목록을 추출하여 가나다 정렬로 반환
 * 공통 교양 개설학과('교양', '교양(야)')는 제외
 */
export function getAllDepartmentsFromCourseData(courseData) {
  if (!Array.isArray(courseData)) return []
  const seen = new Set()
  for (const c of courseData) {
    const dept = c.department?.trim()
    if (dept && !EXCLUDE_DEPT_SET.has(dept)) seen.add(dept)
  }
  return [...seen].sort((a, b) => a.localeCompare(b, 'ko'))
}

/**
 * 이수요건 데이터가 존재하는 학과인지 확인.
 * semester를 넘기면 해당 학기 데이터가 있을 때만 true.
 */
export function hasRequirementData(dept, grade = null, admissionYear = null, semester = null) {
  if (!dept) return false
  return REQUIREMENT_DATA.some(r => {
    if (r.department !== dept) return false
    if (grade != null && r.grade !== grade) return false
    // semester가 지정됐고, 레코드에도 semester가 있을 때만 비교
    if (semester != null && r.semester != null && r.semester !== semester) return false
    return true
  })
}

/**
 * 이수조건 데이터 상태 반환
 * - 'full'         : 1학년 이상 + 2학년 이상 데이터 모두 있음 → 이수조건 있음
 * - 'partial'      : 일부 학년만 데이터 있음 → 일부 이수조건
 * - 'needs_review' : status가 needs_review / missing_required_courses → 검증 필요
 * - 'none'         : requiredCourses가 없는 stub만 있거나 데이터 없음 → 준비 중
 * @returns {'full' | 'partial' | 'needs_review' | 'none'}
 */
export function getRequirementStatus(departmentName) {
  if (!departmentName) return 'none'
  const canonical = normalizeDepartmentName(departmentName)
  const withData = ALL_REQUIREMENTS.filter(
    r => r.departmentName === canonical && r.requiredCourses.length > 0
  )
  if (!withData.length) return 'none'
  if (withData.some(r => r.status === 'needs_review' || r.status === 'missing_required_courses')) {
    return 'needs_review'
  }
  const grades = new Set(withData.map(r => r.grade))
  return grades.size >= 2 ? 'full' : 'partial'
}

/**
 * 학과의 이수조건 고유 과목 수 반환 (courseCode 기준 중복 제거)
 */
export function getRequirementCourseCount(departmentName) {
  if (!departmentName) return 0
  const canonical = normalizeDepartmentName(departmentName)
  const unique = new Set()
  ALL_REQUIREMENTS
    .filter(r => r.departmentName === canonical && r.requiredCourses.length > 0)
    .forEach(r => r.requiredCourses.forEach(c => unique.add(c.courseCode ?? c.courseName)))
  return unique.size
}

/**
 * 이수조건 데이터가 있는 학기 목록 반환 (예: ['1-1', '1-2', '2-1'])
 */
export function getAvailableRequirementSemesters(departmentName) {
  if (!departmentName) return []
  const canonical = normalizeDepartmentName(departmentName)
  return ALL_REQUIREMENTS
    .filter(r => r.departmentName === canonical && r.requiredCourses.length > 0)
    .map(r => `${r.grade}-${r.semester ?? '?'}`)
    .sort()
}

/**
 * 시간표 모드를 반환한다.
 * @returns {'requirement' | 'general'}
 */
export function getTimetableMode(dept, grade = null, admissionYear = null) {
  return hasRequirementData(dept, grade, admissionYear) ? 'requirement' : 'general'
}

/**
 * 학과에서 실제 이수조건 데이터가 존재하는 학년 목록을 반환한다.
 * 이수조건 데이터가 없는 학과(일반 모드)는 [1,2,3,4]를 반환한다.
 * @param {string} dept
 * @returns {number[]} 오름차순 정렬된 학년 배열
 */
export function getAvailableGradesForDept(dept) {
  if (!dept) return [1, 2, 3, 4]
  const grades = [...new Set(
    REQUIREMENT_DATA
      .filter(r => r.department === dept)
      .map(r => r.grade)
  )].sort((a, b) => a - b)
  return grades.length > 0 ? grades : [1, 2, 3, 4]
}

// ── 단과대 / 학과 조회 헬퍼 ──────────────────────────────────────────────────

/** 모든 단과대 목록 반환 */
export function getColleges() {
  return COLLEGES
}

/** 단과대명으로 소속 학과 목록 반환 */
export function getDepartmentsByCollege(collegeName) {
  const col = COLLEGES.find(c => c.collegeName === collegeName)
  return col?.departments ?? []
}

/** 학과명으로 소속 단과대를 반환한다. */
export function getCollegeForDepartment(departmentName) {
  if (!departmentName) return null
  return COLLEGES.find(college => college.departments.some(dept => dept.departmentName === departmentName))?.collegeName ?? null
}

/** 단과대 기준으로 개설 학과 관련 분반인지 확인한다. 학과 미배정 학생 경로에서 사용한다. */
export function isCourseAvailableForCollege(course, collegeName) {
  const college = COLLEGES.find(item => item.collegeName === collegeName)
  if (!college) return false
  return college.departments.some(dept => isCourseAvailableForDept(course, dept.departmentName))
}

// ── 학과명 정규화 ─────────────────────────────────────────────────────────────

/**
 * UI 선택명 / 과거 명칭 → PDF 기준 공식 학과명으로 정규화
 * 매핑 근거: raw-data/requirements/2026_credits.pdf
 */
const DEPT_ALIAS_MAP = {
  // 경영공학대학
  '경영학과':               '경영공학학부',
  '경영공학':               '경영공학학부',
  '경영공학대학':           '경영공학학부',  // courseData 개설학과명
  '경영학부':               '경영공학학부',  // localStorage 역호환

  // 지능형SW대학
  'AI소프트웨어학부':        'AI·소프트웨어학부',
  'AI데이터과학부':          'AI·소프트웨어학부',
  '미디어SW':               'AI·소프트웨어학부',
  '컴퓨터학부':             '컴퓨터공학부',
  '컴퓨터SW':               '컴퓨터공학부',

  // 혁신공과대학
  '전기전자공학부':          '전자공학부',
  '전기공학':               '전자공학부',
  '전자재료공학':           '전자공학부',
  '전자물리':               '전자공학부',
  '화학공학·신소재공학부':  '화학공학과',
  '신소재공학':             '화학공학과',
  '융합화학산업':           '화학공학과',
  '건설환경공학':           '토목환경공학부',
  '건설환경에너지공학부':   '토목환경공학부',
  '환경에너지공학':         '토목환경공학부',

  // 디자인앤아트대학
  '커뮤니케이션디자인':     '디자인학부',
  '패션디자인':             '디자인학부',
  '공예디자인':             '디자인학부',
}

/**
 * 학과명을 시스템 내 표준명으로 정규화.
 * 알 수 없는 이름은 그대로 반환.
 */
export function normalizeDepartmentName(rawName) {
  if (!rawName) return ''
  const trimmed = String(rawName).trim()
  return DEPT_ALIAS_MAP[trimmed] ?? trimmed
}

// requirements2026.js의 departmentName은 PDF 원본 명칭 사용
// courseData.js의 department와 다른 경우를 위한 별도 매핑
const REQ2026_ALIAS_MAP = {
  // courseData → requirements2026 departmentName
  '경영공학학부':         '경영',
  '경영학부':             '경영',  // 역호환
  '컴퓨터공학부':         '컴퓨터공학',
  'AI·소프트웨어학부':    'AI빅데이터',
  'AI데이터과학부':       'AI빅데이터',
  '전자공학부':           '전자공학',
  '전기전자공학부':       '전기공학',
  '화학공학과':           '화학공학',
  '화학공학·신소재공학부':'화학공학',
  '토목환경공학부':       '건설환경공학',
  '건설환경에너지공학부': '건설환경공학',
  '건축도시부동산학부':   '건축학',
  '지능형보안학부':       '정보보안',
  '스포츠과학부':         '체육학',
  '조형예술학부':         '회화',
  '디자인학부':           '커뮤니케이션디자인',
  '컴퓨터학부':           '컴퓨터공학',
  '바이오공학':           '바이오공학과',
  '아동가족복지학':       '아동가족복지학과',
  '의류학':               '의류학과',
  '식품영양학':           '식품영양학과',
  '반도체공학':           '반도체공학과',
  '기계공학':             '기계공학부',
  '경제학부':             '경제금융',
  '국어국문학':           '한국언어문화',
  '러시아어문학':         '러시아언어문화',
}

// requirements2026 departmentName → collegeName (getDeptKeywords가 학부/단과대 단위 공통
// 개설 과목 매칭에 쓴다. REQUIREMENTS_2026은 이미 이 파일 상단에서 import 돼 있다.)
const REQ2026_DEPT_TO_COLLEGE = new Map(REQUIREMENTS_2026.map(d => [d.departmentName, d.collegeName]))

// REQ2026_ALIAS_MAP(courseData 쪽 이름 → requirements2026 학과명)의 역방향 조회.
// 같은 requirements2026 학과명에 courseData 쪽 별칭이 여러 개 걸릴 수 있어 배열로 모은다.
const REQ2026_REVERSE_ALIAS_MAP = new Map()
for (const [courseDataName, req2026Name] of Object.entries(REQ2026_ALIAS_MAP)) {
  if (!REQ2026_REVERSE_ALIAS_MAP.has(req2026Name)) REQ2026_REVERSE_ALIAS_MAP.set(req2026Name, [])
  REQ2026_REVERSE_ALIAS_MAP.get(req2026Name).push(courseDataName)
}

/**
 * courseData/UI 학과명을 requirements2026 departmentName으로 정규화
 */
export function normalizeDeptFor2026(rawName) {
  if (!rawName) return ''
  const trimmed = String(rawName).trim()
  return REQ2026_ALIAS_MAP[trimmed] ?? trimmed
}

/**
 * course.department가 selectedDept와 "정확히" 같은 학과인지 판단한다.
 * getCourseTag의 'my-dept' 태그는 학부/단과대 공통 매칭(getDeptKeywords)까지 함께 묶기 때문에,
 * 그 안에서 정확 일치 학과를 더 우선하고 싶을 때(자동완성 정렬 등) 이 함수로 세분화한다.
 * requirements2026 학과명 기준으로 정규화 후 비교 — 별도 별칭 테이블을 새로 만들지 않고
 * normalizeDeptFor2026(REQ2026_ALIAS_MAP)을 재사용한다.
 */
export function isExactDeptMatch(course, selectedDept) {
  if (!course || !selectedDept) return false
  const dept = normalizeDeptName(course.department)
  if (!dept) return false
  return normalizeDeptFor2026(dept) === normalizeDeptFor2026(selectedDept)
}

// requirements2026 collegeName → 소속 departmentName 목록 (REQ2026_DEPT_TO_COLLEGE의 역방향)
const REQ2026_COLLEGE_TO_DEPTS = new Map()
for (const dept of REQUIREMENTS_2026) {
  if (!REQ2026_COLLEGE_TO_DEPTS.has(dept.collegeName)) REQ2026_COLLEGE_TO_DEPTS.set(dept.collegeName, [])
  REQ2026_COLLEGE_TO_DEPTS.get(dept.collegeName).push(dept.departmentName)
}

/**
 * 단과대 기준으로 개설 분반이 그 단과대 소속인지 확인한다 (requirements2026 8개 단과대/53개 학과
 * 체계 기준). course.department가 단과대명 그대로 찍히는 대규모 공통 개설(예: "경영공학대학")과
 * 특정 학과명으로 찍히는 개설(예: "컴퓨터공학부") 모두를 다룬다.
 * colleges.js의 isCourseAvailableForCollege(구 4개 단과대/8개 학과 체계)와는 별개 함수다 —
 * 그 함수는 getEligibleSectionsForStudent 등 기존 hard-filter 경로에서 계속 쓰이므로 그대로 두고,
 * requirements2026 체계가 필요한 신규 호출부(뱃지 판정 등)는 이 함수를 쓴다.
 */
export function isCourseAvailableForCollege2026(course, collegeName) {
  if (!course || !collegeName) return false
  const dept = normalizeDeptName(course.department)
  if (!dept) return false
  if (dept === normalizeDeptName(collegeName)) return true
  const depts = REQ2026_COLLEGE_TO_DEPTS.get(collegeName)
  if (!depts) return false
  return depts.some(d => getCourseTag(course, d) === 'my-dept')
}

/**
 * 검색 결과 카드 등에 표시할 소속 친연성 뱃지 판정.
 * 1. selectedDept와 정확히 같은 학과(isExactDeptMatch) → 'my-dept'
 * 2. 학과는 다르지만 같은 단과대(같은 단과대 소속 다른 학과, 또는 단과대 단위 공통 개설) → 'my-college'
 * 3. 둘 다 아니면 null (뱃지 없음)
 * selectedCollege가 없으면 selectedDept로부터 REQ2026_DEPT_TO_COLLEGE를 통해 유추한다.
 * @returns {'my-dept' | 'my-college' | null}
 */
export function getAffinityTag(course, selectedDept, selectedCollege) {
  if (!course) return null
  if (selectedDept && isExactDeptMatch(course, selectedDept)) return 'my-dept'
  const college = selectedCollege || (selectedDept ? REQ2026_DEPT_TO_COLLEGE.get(selectedDept) : null)
  if (college && isCourseAvailableForCollege2026(course, college)) return 'my-college'
  return null
}

/**
 * 학과명으로 requirements2026 졸업/교양/전공 학점 요약 반환.
 * courseData 이름과 requirements2026 이름 모두 허용.
 * @returns {{ graduationCredits, liberalArtsTotal, majorRequired, majorElective, majorTotal } | null}
 */
export function getCreditSummary2026(departmentName) {
  if (!departmentName) return null
  const canonical = normalizeDeptFor2026(departmentName)
  const result = getCreditRequirements2026(canonical)
  if (result) return result
  // 원본 이름으로도 재시도
  return getCreditRequirements2026(departmentName)
}

/**
 * 학과명으로 requirements2026 전체 데이터 반환
 */
export function getDeptData2026(departmentName) {
  if (!departmentName) return null
  const canonical = normalizeDeptFor2026(departmentName)
  return getDeptRequirements2026(canonical) ?? getDeptRequirements2026(departmentName)
}

/**
 * 단과대학 목록 및 각 단과대학의 학과 목록 반환 (requirements2026 기준)
 */
export function getColleges2026() {
  const map = new Map()
  for (const dept of REQUIREMENTS_2026) {
    if (!map.has(dept.collegeName)) {
      map.set(dept.collegeName, { collegeId: dept.collegeId, collegeName: dept.collegeName, departments: [] })
    }
    map.get(dept.collegeName).departments.push({
      departmentId:   dept.departmentId,
      departmentName: dept.departmentName,
      icon:           dept.icon,
      graduationCredits: dept.graduationCredits,
      majorRequired:  dept.majorCredits.majorRequired,
    })
  }
  return [...map.values()]
}

// ── 졸업 이수요건 조회 ────────────────────────────────────────────────────────

/**
 * 학과명으로 2026학년도 졸업 이수학점 요건 반환.
 * normalizeDepartmentName으로 별칭도 해결한다.
 *
 * @param {{ department: string, admissionYear?: number }} params
 * @returns {object|null}
 */
export function getGraduationRule({ department, admissionYear } = {}) {
  if (!department) return null
  const canonical = normalizeDepartmentName(department)
  const entry = Object.values(GRADUATION_RULES_2026).find(
    r => typeof r === 'object' && r.departmentName === canonical
  )
  return entry ?? null
}

/**
 * 단과대 메타 정보 반환 (colleges.js 래퍼)
 */
export function getDeptMeta(departmentName) {
  return DEPT_META_MAP[departmentName] ?? null
}
